import { ensureCsrfToken, readCsrfTokenFromResponse, withCsrfHeader } from '../common/utils/auth/csrf'
import { requestPortalSessionReset } from '../common/utils/portalSessionReset'

import http, { Query, Method } from './http'
import getChargeAmpsApiPath from './getChargeAmpsApiPath'
import { isPortalDomain } from './currentDomain'
import {
    shouldAttachPortalCsrf,
    shouldInvalidatePortalSessionOnUnauthorized,
    shouldRetryPortalRequestWithFreshCsrf,
} from './portalRequestPolicy'

const isResponseLike = (error: unknown): error is Response =>
    typeof error === 'object' && error !== null && 'status' in error && 'text' in error

export const authInterceptor = (response: Response): Response => {
    const responsePath = new URL(response.url).pathname

    if (shouldInvalidatePortalSessionOnUnauthorized({ status: response.status, url: responsePath })) {
        requestPortalSessionReset()
    }
    return response
}

export const defaultParseResponse = <APIResponse>(response: Response): Promise<APIResponse> =>
    response
        .text()
        .then((text) => {
            if (!text) return null
            return JSON.parse(text)
        })
        .then((result) => {
            if (response.status < 400) return result
            return Promise.reject(result)
        })

function api<APIResponse = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown> | string>({
    method,
    path,
    base = getChargeAmpsApiPath(),
    query,
    body,
    headers,
    parseResponse,
}: Props<APIResponse, Body>): Promise<APIResponse> {
    const cookieAuthMode = isPortalDomain
    const url = `${base}${path.startsWith('/') ? path : `/${path}`}`

    const execute = async (forceCsrfRefresh = false) => {
        let requestHeaders: Headers | string[][] | Record<string, string> = {
            ...(!(body instanceof FormData) &&
                typeof body !== 'string' && {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                }),
            ...headers,
        }

        if (shouldAttachPortalCsrf({ cookieAuthMode, method, url: path })) {
            const csrfToken = await ensureCsrfToken(
                async () => {
                    const response = await http<Response>({
                        url: `${getChargeAmpsApiPath()}/csrf/handshake`,
                        method: 'GET',
                        credentials: 'include',
                    })

                    if ((response as Response).status >= 400) {
                        throw response as Response
                    }

                    return readCsrfTokenFromResponse(response as Response)
                },
                { forceRefresh: forceCsrfRefresh }
            )

            requestHeaders = withCsrfHeader(requestHeaders, csrfToken)
        }

        return http({
            url,
            query,
            method,
            headers: requestHeaders,
            body: body instanceof FormData || typeof body === 'string' ? body : JSON.stringify(body),
            credentials: cookieAuthMode ? 'include' : undefined,
        })
    }

    const executeWithHttpErrorRecovery = (forceCsrfRefresh = false) =>
        execute(forceCsrfRefresh).catch((error) => {
            if (isResponseLike(error)) return error
            throw error
        })

    return executeWithHttpErrorRecovery()
        .then((response) => {
            if (shouldRetryPortalRequestWithFreshCsrf({ cookieAuthMode, method, url: path, status: response.status })) {
                return executeWithHttpErrorRecovery(true)
            }
            return response
        })
        .then(authInterceptor)
        .then((response) => {
            if (parseResponse) return parseResponse(response, defaultParseResponse)
            return defaultParseResponse<APIResponse>(response)
        })
}

export default Object.assign(api, {
    get: <APIResponse = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown> | string>(
        props: Omit<Props<APIResponse, Body>, 'method'>
    ): Promise<APIResponse> => api<APIResponse, Body>({ ...props, method: 'GET' }),

    post: <APIResponse = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown> | string>(
        props: Omit<Props<APIResponse, Body>, 'method'>
    ): Promise<APIResponse> => api<APIResponse, Body>({ ...props, method: 'POST' }),

    put: <APIResponse = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown> | string>(
        props: Omit<Props<APIResponse, Body>, 'method'>
    ): Promise<APIResponse> => api<APIResponse, Body>({ ...props, method: 'PUT' }),

    del: <APIResponse = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown> | string>(
        props: Omit<Props<APIResponse, Body>, 'method'>
    ): Promise<APIResponse> => api<APIResponse, Body>({ ...props, method: 'DELETE' }),
})

export type Props<APIResponse, Body> = {
    path: string
    base?: string
    query?: Query
    method?: Method
    headers?: Record<string, string>
    body?: Body
    parseResponse?: (response: Response, defaultParser: (response: Response) => Promise<APIResponse>) => APIResponse
}

export type { Query, Method }
